/**
 * English is the source catalogue: every other locale is typed against its
 * shape, so a key added here without a translation elsewhere is a build error.
 *
 * The "Avalara" wordmark and the "Trade Compliance" product name are not keyed.
 * They are brand identity rather than copy, and keying them invites translators
 * to render the product under a different name in each locale.
 */
const en = {
  common: {
    cancel: 'Cancel',
    create: 'Create',
    save: 'Save',
    saving: 'Saving...',
    refresh: 'Refresh',
    loading: 'Loading...'
  },
  nav: {
    products: 'Products',
    upload: 'Upload',
    adhoc: 'Ad-Hoc',
    reports: 'Reports',
    settings: 'Settings'
  },
  sidebar: {
    expand: 'Expand',
    collapse: 'Collapse',
    expandSidebar: 'Expand sidebar',
    collapseSidebar: 'Collapse sidebar',
    logout: 'Logout'
  },
  topbar: {
    welcome: 'Welcome, {name}',
    selectLanguage: 'Select language'
  },
  footer: {
    copyright: 'Copyright © {year} Avalara Inc. All rights reserved.'
  },
  auth: {
    redirecting: 'Redirecting to login...',
    completing: 'Completing authentication...',
    noCode: 'No authorization code received',
    failed: 'Authentication failed. Please try again.',
    tryAgain: 'Try Again'
  },
  settings: {
    profiles: 'Profiles',
    users: 'Users'
  },
  reporting: {
    userTitle: 'User Reports',
    userSubtitle: 'View and download reports related to your activity',
    userEmpty: 'There are no reports available at this time.',
    profileTitle: 'Profile Reports',
    profileSubtitle: 'View and download reports related to your profile activity',
    profileEmpty: 'There are no profile reports available at this time.',
    loading: 'Loading reports...',
    emptyTitle: 'No Reports Available',
    download: 'Download',
    loadFailed: 'Failed to load reports'
  },
  eccn: {
    title: 'Self-Serve ECCN Classification',
    subtitle: 'Enter your product description to get an automated ECCN classification',
    productLabel: 'Product Description',
    productPlaceholder: 'Enter detailed product description, specifications, and intended use...',
    descriptionRequired: 'Please enter a product description',
    clear: 'Clear',
    classify: 'Classify Product',
    classifying: 'Classifying...',
    resultTitle: 'Classification Result',
    confidence: '{value}% Confidence',
    code: 'ECCN',
    description: 'Description',
    controlReason: 'Control Reason',
    exportRestrictions: 'Export Restrictions',
    noRestrictions: 'No specific restrictions',
    downloadReport: 'Download Report',
    saveClassification: 'Save Classification',
    readyTitle: 'Ready to Classify',
    readyHint: 'Enter a product description and click "{action}" to get started'
  },
  dashboard: {
    totalProducts: 'Total Products',
    totalProductsChange: '+12% from last month',
    classifications: 'Classifications',
    classificationsChange: 'Checked products today',
    activeUsers: 'Active Users',
    activeUsersChange: 'Currently online',
    complianceRate: 'Compliance Rate',
    complianceRateChange: '+2.1% improvement',
    quickUpload: 'Quick Upload',
    quickUploadHint: 'Upload and classify new products quickly',
    dropFiles: 'Drag and drop your files here',
    browseFiles: 'Browse Files',
    recentActivity: 'Recent Activity',
    recentActivityHint: 'Latest classification activities',
    classifiedSuccessfully: 'Classified successfully',
    pendingReview: 'Pending review',
    hoursAgo: '{count}h ago',
    daysAgo: '{count}d ago',
    systemStatus: 'System Status',
    systemStatusHint: 'Current status of trade compliance services',
    serviceClassificationApi: 'Classification API',
    serviceFileUpload: 'File Upload',
    serviceReporting: 'Reporting',
    operational: 'Operational',
    issues: 'Issues'
  },
  profiles: {
    addChild: 'Add child profile',
    idLabel: 'ID: {id}',
    information: 'Profile Information',
    nameLabel: 'Profile Name',
    namePlaceholder: 'Enter profile name',
    parentIdLabel: 'Parent ID',
    noParent: 'None (Root)',
    save: 'Save Profile',
    emptyTitle: 'Select a Profile',
    emptyHint: 'Select a profile from the tree to view and edit details',
    createTitle: 'Create Child Profile',
    loadFailed: 'Failed to load profiles',
    detailsFailed: 'Failed to load profile details',
    saved: 'Profile saved successfully',
    saveFailed: 'Failed to save profile',
    nameRequired: 'Please provide a profile name',
    created: 'Profile created successfully',
    duplicateName: 'Profile with this name already exists',
    createFailed: 'Failed to create profile'
  },
  users: {
    count: '{count} user | {count} users',
    addNew: 'Add New User',
    edit: 'Edit User',
    username: 'Username',
    email: 'Email',
    firstName: 'First Name',
    lastName: 'Last Name',
    role: 'Role',
    roleUser: 'User',
    roleAdmin: 'Admin',
    status: 'Status',
    active: 'Active',
    inactive: 'Inactive',
    lastLogin: 'Last Login',
    lastLogout: 'Last Logout',
    assignedProfiles: 'Assigned Profiles',
    loadingProfiles: 'Loading profiles...',
    resetPassword: 'Reset Password',
    loadFailed: 'Failed to load users',
    requiredFields: 'Username, first name and email are required',
    profileRequired: 'Please select at least one profile',
    usernameExists: 'Username already exists',
    saved: 'User saved successfully',
    saveFailed: 'Failed to save user',
    passwordReset: 'Password reset successfully',
    passwordResetFailed: 'Failed to reset password'
  },
  upload: {
    colUploaded: 'Uploaded',
    colClient: 'Client',
    colProfile: 'Profile',
    colDescription: 'Description',
    colFileName: 'File Name',
    colQuantity: 'Quantity',
    colStatus: 'Status',
    colErrors: 'Errors',
    stateNew: 'New',
    statePending: 'Pending',
    stateRunning: 'Running',
    stateProcessing: 'Processing',
    stateCompleted: 'Completed',
    stateError: 'Error',
    countProcessed: 'Processed: {processed} of {total}',
    countSuccess: 'Success: {count}',
    countErrors: 'Errors: {count}',
    countWarnings: 'Warnings: {count}',
    configTitle: 'Upload Configuration',
    configSubtitle: 'Configure your upload settings',
    clientLabel: 'Client',
    clientPlaceholder: 'Client name',
    profileLabel: 'Profile',
    profilePlaceholder: 'Select profile...',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'Enter description for this upload...',
    destinationsLabel: 'Destinations',
    destinationsPlaceholder: 'Select destinations...',
    duplicateStrategy: 'Duplicate Strategy',
    overwriteDuplicates: 'Overwrite duplicates',
    ignoreDuplicates: 'Ignore duplicates',
    actionsLabel: 'Actions',
    classification: 'Classification',
    fileTitle: 'File Upload',
    fileSubtitle: 'Drag and drop or select files to upload',
    dropTitle: 'Drag and drop your files here',
    formatHint: 'Must be in excel format (.xlsx). To download a sample template,',
    clickHere: 'click here',
    headerRowHint: 'The first row of the input file is reserved as a header row',
    selectFile: 'Select file to upload',
    selectAll: 'Select All',
    deselectAll: 'Deselect All',
    uploadedFiles: 'Uploaded Files',
    autoRefresh: 'Auto-refresh',
    confirmTitle: 'Confirm Upload',
    confirmBody: 'This file will be uploaded to {profile}. Are you sure?',
    uploadAction: 'Upload',
    listFailed: 'Failed to load file list',
    xlsxOnly: 'Only .xlsx files are allowed',
    tooLarge: 'File size must be less than 10MB',
    processingStarted: 'Saving file and starting processing...',
    saved: 'File saved and will be processed shortly',
    uploadFailed: 'Failed to upload file',
    templateFailed: 'Failed to download template'
  },
  reports: {
    userLabel: 'User',
    profileLabel: 'Profile',
    from: 'From',
    to: 'To',
    exporting: 'Exporting...',
    exportDetails: 'Export Details',
    productStatistics: 'Product Statistics',
    addedProducts: 'Added Products:',
    attemptedClassifications: 'Attempted Classifications:',
    classified: 'Classified:',
    unclassified: 'Unclassified:',
    classifiedLabel: 'Classified',
    noData: 'No Data Available',
    noDataShort: 'No data',
    classificationsOverTime: 'Classifications Over Time',
    totalAttempted: 'Total Attempted Classifications:',
    classificationsTooltip: '{count} classifications',
    topItems: 'Top 5 Items',
    topItemsHint: 'Top 5 items across all products',
    topCategories: 'Top 5 Categories',
    topCategoriesHint: 'Top 5 categories for un-classified products',
    topCodes: 'Top 5 Codes',
    topCodesHint: 'Top 5 codes for classified products',
    exported: 'Report exported successfully',
    exportFailed: 'Failed to export report'
  },
  adhoc: {
    title: 'Product Classification',
    subtitle: 'Enter product details below to get instant HS code classification.',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'Describe product here...',
    descriptionHelp: 'Provide a detailed description of the product including materials, purpose, and key characteristics.',
    originLabel: 'Origin',
    originPlaceholder: 'Select origin...',
    destinationLabel: 'Destination',
    destinationPlaceholder: 'Select destination...',
    classify: 'Classify',
    classifying: 'Classifying...',
    reset: 'Reset',
    tipsTitle: 'Classification Tips',
    tipsBestResults: 'For Best Results:',
    tipMaterial: 'Include material composition',
    tipUse: 'Specify intended use or function',
    tipCharacteristics: 'Mention key physical characteristics',
    tipBrand: 'Include brand or model if relevant',
    tipsExamples: 'Common Examples:',
    exampleShirt: 'Cotton t-shirt for men, size L',
    exampleKnife: 'Stainless steel kitchen knife, 8 inch',
    exampleBattery: 'Lithium-ion battery for smartphones',
    exampleTable: 'Wooden dining table, oak, 6 seats',
    fieldDescription: 'product description',
    fieldOrigin: 'origin',
    fieldDestination: 'destination',
    missingFields: 'Please fill all required fields: {fields}',
    classificationTitle: 'Classification',
    classificationTitleFor: 'Classification: {description}',
    completed: 'Classification completed',
    completedCreateRule: 'Classification completed. Create your rule now.',
    classifierNotStarted: "The classification window didn't load. Click to try again.",
    missingHsCode: 'Missing HS Code for quoting',
    quoteTitle: 'Duty & Tax Quote',
    quoteConfirmHeading: 'Do you want to calculate duties and taxes?',
    quoteConfirmBody: 'This will allow you to calculate estimated duties and taxes for the classified product.',
    classificationSummary: 'Classification Summary',
    hsCodeLabel: 'HS Code:',
    originValueLabel: 'Origin:',
    destinationValueLabel: 'Destination:',
    stackedHsCodes: 'Stacked HS Codes:',
    noCancel: 'No, Cancel',
    yesContinue: 'Yes, Continue',
    quoteParamsTitle: 'Enter Quoting Parameters',
    classificationDetails: 'Classification Details',
    optionalParameters: 'Optional Parameters',
    shipmentType: 'Shipment Type',
    adValorem: 'Ad Valorem',
    specific: 'Specific',
    currency: 'Currency',
    countryOfOrigin: 'Country of Origin',
    selectCoo: 'Select COO...',
    quantity: 'Quantity',
    unitValue: 'Unit Value ($)',
    total: 'Total ($)',
    shipping: 'Shipping ($)',
    handling: 'Handling ($)',
    insurance: 'Insurance ($)',
    measureValue: 'Measure Value',
    unit: 'Unit',
    calculating: 'Calculating...',
    getQuote: 'Get Quote',
    quoteResultsTitle: 'Duty & Tax Quote Results',
    quoteSummary: 'Quote Summary',
    quoteIdLabel: 'Quote ID:',
    typeLabel: 'Type:',
    shipmentLabel: 'Shipment:',
    currencyLabel: 'Currency:',
    lineHeading: 'Line {number} - {description}',
    hsBadge: 'HS: {code}',
    duties: 'Duties',
    taxes: 'Taxes',
    colDescription: 'Description',
    colAmount: 'Amount',
    colType: 'Type',
    colTarget: 'Target',
    colRate: 'Rate',
    dutyDetails: 'Duty Details & HS Code Application',
    rateLabel: 'Rate:',
    methodLabel: 'Method:',
    labelLabel: 'Label:',
    totalDuty: 'Total Duty',
    totalTax: 'Total Tax',
    grandTotal: 'Grand Total',
    disclaimerLabel: 'Disclaimer:',
    disclaimerBody: 'These estimates are for informational purposes only and may not reflect actual duties and taxes charged at import.',
    close: 'Close'
  },
  products: {
    tabDetails: 'Details',
    tabDestinations: 'Destinations',
    tabHistory: 'History',
    tabAudit: 'Audit',
    tabCopy: 'Copy',
    colDate: 'Date',
    colId: 'ID',
    colProfile: 'Profile',
    colDescription: 'Description',
    colCustomsDesc: 'Customs Desc',
    colItem: 'Item',
    colCategory: 'Category',
    colDutyRates: 'Duty rates',
    productId: 'Product ID',
    description: 'Description',
    customsDescription: 'Customs Description',
    client: 'Client',
    source: 'Source',
    origin: 'Origin',
    selectOrigin: 'Select origin...',
    manufacturerName: 'Manufacturer Name',
    reclassify: 'Re-classify',
    classify: 'Classify',
    reset: 'Reset',
    save: 'Save',
    selectProductDetails: 'Select a product to view details',
    tariffPackageRequired: 'To access this feature, you need the tariff content data package.',
    destination: 'Destination',
    selectDestination: 'Select destination...',
    hsCode: 'HS Code',
    lookupTariffCode: 'Lookup Tariff Code',
    tariffCode: 'Tariff Code',
    addTariffCode: 'Add Tariff Code',
    viewCompliance: 'View compliance details',
    removeDestination: 'Remove destination',
    valid: 'Valid',
    invalid: 'Invalid',
    restrictions: 'Restrictions',
    selectProductDestinations: 'Select a product to view destinations',
    noHistory: 'No history available',
    questions: 'Questions',
    rationale: 'Rationale',
    hsVersion: 'HS Version',
    noAudit: 'No audit information available',
    copyFields: 'Copy fields',
    copyTo: 'Copy to',
    checkedProducts: "The products I've checked off",
    allListedProducts: 'All currently listed products',
    productCount: '{count} products',
    whenDataExists: 'When data already exists',
    overwrite: 'Overwrite existing data',
    doNotOverwrite: 'Do not overwrite existing data',
    copying: 'Copying...',
    copy: 'Copy',
    selectProductCopy: 'Select a product to configure copy options',
    loadingProduct: 'Loading product information...',
    matching: 'Matching: {count}',
    checked: 'Checked: {count}',
    exportAction: 'Export',
    reclassifyAction: 'Re-Classify',
    addProduct: 'Add Product',
    filters: 'Filters',
    newProduct: 'New Product',
    productIdRequired: 'Product ID is required.',
    descriptionRequired: 'Description is required.',
    profile: 'Profile',
    selectProfile: 'Select profile...',
    reclassifyTitle: 'Re-Classify Products',
    reclassifyConfirm: 'Are you sure you want to re-classify {count} products?',
    reclassifyNeedsSelection: 'Check the products to re-classify first',
    reclassifyScopeQuestion: 'Which products do you want to re-classify?',
    reclassifyScopeChecked: "Only the products I've checked",
    reclassifyScopeAll: 'All products matching the filter, on every page',
    reclassifyWarning: 'This may impact other users until the process completes.',
    reclassifyProgress: 'Re-classifying products... {count}',
    processing: 'Processing...',
    complianceTitle: 'Compliance Information',
    add: 'ADD',
    cvd: 'CVD',
    pga: 'PGA',
    yes: 'YES',
    no: 'NO',
    mfnRate: 'MFN Rate',
    exportTitle: 'Export Products',
    exportConfirm: 'Do you want to export the current list of products (according to the active filters) to Excel?',
    exportHint: 'The file will be generated and your browser will start the download automatically.',
    preparingExcel: 'Preparing Excel file. Your browser will start the download shortly.',
    dutyRatesTitle: 'Duty Rates',
    loadingDutyRates: 'Loading duty rates...',
    dutyRateDetails: 'Duty rate details',
    noDetailedDutyRates: 'No detailed duty rates available.',
    noDutyRates: 'No duty rates available for this product.',
    classifiedHsVersion: 'Classified / HS Version',
    all: 'All',
    classified: 'Classified',
    notClassified: 'Not Classified',
    hsYear: 'HS Year',
    item: 'Item',
    category: 'Category',
    fromTo: 'From / To',
    search: 'Search',
    dutyRatesFailed: 'Failed to load duty rates',
    detailsFailed: 'Failed to load product details',
    saved: 'Product saved successfully',
    saveFailed: 'Failed to save product',
    profileNotReady: 'User profile is not ready for copy',
    noCopyTargets: 'No target products selected to copy to',
    noCopySources: 'No source fields available to copy',
    copied: 'Fields copied successfully',
    copyFailed: 'Failed to copy fields to products',
    copyStillRunning: 'The copy has not finished and may still be running on the server. Check the products before copying again.',
    copyLeaveConfirm: "A copy is in progress. If you leave this page, the copy carries on on the server, but you won't see whether it succeeded. Leave anyway?",
    createRequiredFields: 'Product ID and Description are required',
    created: 'Product created successfully',
    createFailed: 'Failed to create product',
    classifyRequirements: 'Customs Description or an existing HS Code is required to classify this product',
    classifying: 'Classifying product...',
    noHsCode: 'No HS code returned from classifier',
    noHsYear: 'Classification returned without HS year information',
    classifiedOk: 'Product classified successfully',
    classifyCancelled: 'Classification cancelled or failed',
    classifierNotStarted: "The classification window didn't load. Click to try again.",
    reclassifyStarted: 'Re-classification started',
    reclassifyFailed: 'Failed to start re-classification',
    exportFailed: 'Export failed',
    complianceFailed: 'Failed to load compliance data',
    tariffSaved: 'Tariff code saved',
    tariffSaveFailed: 'Failed to save tariff code',
    tariffSuggested: 'Tariff code suggested from classifier',
    noTariffCode: 'No tariff code returned from classifier',
    tariffLookupFailed: 'Tariff lookup failed',
    deletingDestination: 'Deleting destination {destination}...',
    destinationDeleted: 'Destination deleted',
    destinationDeleteFailed: 'Failed to delete destination'
  },
  rules: {
    title: 'Classification Rules',
    subtitle: 'View saved classification rules that auto-fill answers during product classification.',
    count: 'You have {count} rules',
    create: 'Create Rule',
    classifyFirst: 'Classify a product first to create a rule.',
    filterPlaceholder: 'Filter by name or item...',
    filterAria: 'Filter rules',
    rowHint: 'Click a row to view rule details',
    colRuleName: 'Rule Name',
    colItem: 'Item',
    colSip: 'SIP',
    detailsTitle: 'Rule Details',
    ruleNameLabel: 'Rule Name:',
    itemLabel: 'Item:',
    sipLabel: 'SIP:',
    constraints: 'Constraints',
    constraintsCaption: 'Constraints for the selected rule',
    colCategory: 'Category',
    colType: 'Type',
    colValues: 'Values',
    typeSelection: 'Selection',
    typeValued: 'Valued',
    deleting: 'Deleting...',
    deleteRule: 'Delete Rule',
    close: 'Close',
    createTitle: 'Create Classification Rule',
    ruleNameField: 'Rule Name',
    ruleNamePlaceholder: 'e.g. Cotton T-Shirts',
    itemNameField: 'Item Name',
    itemNamePlaceholder: 'e.g. T-shirt',
    sipField: 'SIP',
    sipPlaceholder: 'e.g. 6109.10',
    hsCodeLabel: 'HS Code:',
    originLabel: 'Origin:',
    destinationLabel: 'Destination:',
    interactions: 'Interactions',
    selectAll: 'Select all',
    unselectAll: 'Unselect all',
    selectedCount: 'Selected interactions: {count}',
    ruleApplied: 'Rule applied',
    ruleMismatch: 'Rule mismatch',
    category: 'Category',
    type: 'Type',
    values: 'Values',
    select: 'Select',
    deleteTitle: 'Delete Rule',
    deleteConfirm: 'This action cannot be undone. Delete {name}?',
    delete: 'Delete',
    classifyBeforeCreate: 'Please classify a product before creating a rule.',
    noProfile: 'No profile selected for rule operations',
    requiredFields: 'Rule name, item and SIP are required',
    selectInteraction: 'Select at least one interaction',
    itemInteractionRequired: 'This rule cannot be saved without selecting the item interaction.',
    selectOneOption: 'Select one option for "{label}"',
    enterValues: 'Enter values for "{label}"',
    valuesMustTotal: 'Values for "{label}" must total 100%',
    created: 'Rule created successfully',
    createFailed: 'Failed to create rule',
    deleted: 'Rule deleted successfully',
    deleteFailed: 'Failed to delete rule'
  },
  table: {
    noData: 'No data available',
    downloadErrorFile: 'Download error file',
    showing: 'Showing {from} - {to} of {total}',
    perPage: '{size} per page',
    requiresAccess: 'This feature requires additional access'
  },
  header: {
    greeting: 'Hello {name}'
  }
}

export type MessageSchema = typeof en

export default en
